import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider
} from '@mui/material';
import { useParams } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';

// Dados de exemplo para demonstração
const mockEvent = {
  id: '1',
  title: 'Retiro Espiritual 2025',
  description: 'Retiro anual da paróquia com o tema "Renovação". Três dias de oração, reflexão e convivência em um ambiente de paz e espiritualidade.',
  startDate: new Date('2025-06-15T08:00:00'),
  endDate: new Date('2025-06-17T18:00:00'),
  location: 'Sítio Santa Maria - Estrada do Campo, km 5, Zona Rural',
  capacity: 100,
  registeredCount: 45,
  isPaid: true,
  registrationDeadline: new Date('2025-06-10T23:59:59'),
  category: 'retiro',
  status: 'published',
  imageUrl: 'https://source.unsplash.com/random/800x400/?retreat',
  parish: {
    name: 'Paróquia São Francisco',
    city: 'São Paulo',
    state: 'SP'
  },
  tickets: [
    {
      id: '101',
      name: 'Ingresso Padrão',
      description: 'Inclui hospedagem e alimentação',
      price: 150.00,
      quantity: 80,
      soldCount: 35,
      availableQuantity: 45
    },
    {
      id: '102',
      name: 'Ingresso VIP',
      description: 'Inclui hospedagem, alimentação e kit especial',
      price: 250.00,
      quantity: 20,
      soldCount: 10,
      availableQuantity: 10
    }
  ]
};

const EventDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState(mockEvent);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  
  // Aqui seria feita a chamada à API para buscar os detalhes do evento
  // useEffect(() => {
  //   const fetchEvent = async () => {
  //     try {
  //       const response = await api.get(`/events/${id}`);
  //       setEvent(response.data.event);
  //     } catch (error) {
  //       console.error('Erro ao buscar evento:', error);
  //     }
  //   };
  //   
  //   fetchEvent();
  // }, [id]);
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatDateRange = () => {
    if (event.startDate.toDateString() === event.endDate.toDateString()) {
      // Mesmo dia
      return `${formatDate(event.startDate)} • ${formatTime(event.startDate)} - ${formatTime(event.endDate)}`;
    } else {
      // Dias diferentes
      return `${formatDate(event.startDate)} ${formatTime(event.startDate)} - ${formatDate(event.endDate)} ${formatTime(event.endDate)}`;
    }
  };
  
  const handleTicketSelect = (ticketId: string) => {
    setSelectedTicket(ticketId === selectedTicket ? null : ticketId);
  };
  
  const handleRegister = () => {
    if (!selectedTicket) {
      alert('Por favor, selecione um ingresso');
      return;
    }
    
    // Aqui seria feita a navegação para a página de inscrição
    console.log('Registrando para o evento com o ingresso:', selectedTicket);
  };
  
  const isRegistrationOpen = () => {
    const now = new Date();
    return event.status === 'published' && now <= event.registrationDeadline;
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, mb: 4 }}>
        <CardMedia
          component="img"
          height="300"
          image={event.imageUrl}
          alt={event.title}
          sx={{ objectFit: 'cover' }}
        />
        
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" component="h1" gutterBottom>
                {event.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {formatDateRange()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {event.location}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {event.registeredCount} / {event.capacity} inscritos
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Sobre o Evento
              </Typography>
              <Typography variant="body1" paragraph>
                {event.description}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Organizado por: {event.parish.name} • {event.parish.city}/{event.parish.state}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Inscrições
                </Typography>
                
                {isRegistrationOpen() ? (
                  <>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Inscrições abertas até {formatDate(event.registrationDeadline)} às {formatTime(event.registrationDeadline)}
                    </Typography>
                    
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Selecione seu ingresso:
                    </Typography>
                    
                    {event.tickets.map((ticket) => (
                      <Card 
                        key={ticket.id} 
                        variant="outlined" 
                        sx={{ 
                          mb: 2, 
                          cursor: 'pointer',
                          border: selectedTicket === ticket.id ? '2px solid' : '1px solid',
                          borderColor: selectedTicket === ticket.id ? 'primary.main' : 'divider',
                        }}
                        onClick={() => handleTicketSelect(ticket.id)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="subtitle1">
                                {ticket.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {ticket.description}
                              </Typography>
                            </Box>
                            <Typography variant="h6" color="primary">
                              {event.isPaid ? `R$ ${ticket.price.toFixed(2)}` : 'Gratuito'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {ticket.availableQuantity} disponíveis
                            </Typography>
                            {ticket.availableQuantity === 0 && (
                              <Chip label="Esgotado" size="small" color="error" />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                    
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      sx={{ mt: 2 }}
                      disabled={!selectedTicket}
                      onClick={handleRegister}
                    >
                      Inscrever-se
                    </Button>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body1" color="error" gutterBottom>
                      Inscrições encerradas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      O prazo para inscrições neste evento já passou.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default EventDetailsPage;
